import cn from 'classnames';
import { useContext, useMemo } from 'react';

import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import {
    SUPPORTED_AUDIO_FORMATS,
    SUPPORTED_IMAGE_FORMATS,
    SUPPORTED_VIDEO_FORMATS,
} from '@webapp/common/components/file-manager/lib';
import { ImageWithGallery } from '@webapp/common/components/image-with-gallery';
import { CssQuestionType } from '@webapp/common/components/survey-custom';
import { CDN_URL } from '@webapp/common/conf';
import { setHtml } from '@webapp/common/lib/ui';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';

import css from './text-block.css';

export const TextBlock: VFC<SurveyQuestion> = ({
    question: {
        params: { textBlock, imagePosition },
        filePath,
    },
    questionBlockPosition,
}) => {
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const {
        question: { text: questionTextStyles },
    } = useContext(CustomStylesCtx);

    const fileExt = useMemo(() => {
        if (!filePath) return;

        const split = filePath.split('.');
        return split[split.length - 1];
    }, [filePath]);

    return (
        <div
            className={cn(CssQuestionType.QUESTION, CssQuestionType.TEXT_BLOCK, css.textBlock)}
            style={{ ...questionTextStyles, textAlign: questionBlockPosition }}
        >
            <div {...setHtml(pipingAnswersReplacer(textBlock))}></div>
            {filePath && (
                <div className={css.additionalContent}>
                    {SUPPORTED_IMAGE_FORMATS.includes(fileExt) && (
                        <ImageWithGallery imageUrl={filePath} overflowByHeight={imagePosition === 'by-width'} />
                    )}
                    {SUPPORTED_VIDEO_FORMATS.includes(fileExt) && (
                        <video src={CDN_URL + filePath} className={css.video} controls={true} controlsList='nodownload'>
                            Your browser does not support the video tag.
                        </video>
                    )}
                    {SUPPORTED_AUDIO_FORMATS.includes(fileExt) && (
                        <audio
                            src={CDN_URL + filePath}
                            className={css.audio}
                            controls={true}
                            controlsList='nodownload'
                        ></audio>
                    )}
                </div>
            )}
        </div>
    );
};
TextBlock.displayName = 'TextBlock';
